/**
 * Company API Functions
 *
 * API functions for company CRUD operations
 */

import type {
  Company,
  CreateCompanyDto,
  UpdateCompanyDto,
  CompanyStatus,
  CompanyHierarchyNode,
  LogoVariant,
} from '@lognai/plugin-admin';
import { apiFetch, apiUpload, buildQueryString, type PaginatedListResponse } from './client';
import type { ListQueryParams } from '@lognai/frontend-core';

const BASE_URL = '/api/companies';

/**
 * Company filter fields
 */
export interface CompanyFilter {
  search?: string;
  status?: CompanyStatus;
  type?: string;
  parentCompanyId?: string;
}

/**
 * Company query params
 */
export type CompanyQueryParams = ListQueryParams<CompanyFilter>;

/**
 * Visible company filter fields
 */
export interface VisibleCompanyFilter {
  search?: string;
  status?: CompanyStatus;
  type?: string;
}

/**
 * Visible company query params
 */
export type VisibleCompanyQueryParams = ListQueryParams<VisibleCompanyFilter>;

/**
 * Get list of companies with pagination
 */
export const listCompanies = async (
  params?: CompanyQueryParams
): Promise<PaginatedListResponse<Company>> => {
  const query = buildQueryString(params as Record<string, unknown>);
  return apiFetch<PaginatedListResponse<Company>>(`${BASE_URL}${query}`);
};

/**
 * Get visible companies — the companies owned by the current user's tenant.
 * (Platform admins receive the full cross-tenant list.)
 */
export const listVisibleCompanies = async (
  params?: VisibleCompanyQueryParams
): Promise<PaginatedListResponse<Company>> => {
  const query = buildQueryString(params as Record<string, unknown>);
  return apiFetch<PaginatedListResponse<Company>>(`${BASE_URL}/visible${query}`);
};

/**
 * Get root companies (no parent)
 */
export const getRootCompanies = async (): Promise<Company[]> => {
  return apiFetch<Company[]>(`${BASE_URL}/roots`);
};

/**
 * Get a single company by ID
 */
export const getCompany = async (id: string): Promise<Company> => {
  return apiFetch<Company>(`${BASE_URL}/${id}`);
};

/**
 * Get company by slug
 */
export const getCompanyBySlug = async (slug: string): Promise<Company> => {
  return apiFetch<Company>(`${BASE_URL}/slug/${slug}`);
};

/**
 * Create a new company
 */
export const createCompany = async (dto: CreateCompanyDto): Promise<Company> => {
  return apiFetch<Company>(BASE_URL, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
};

/**
 * Update an existing company
 */
export const updateCompany = async (
  id: string,
  dto: UpdateCompanyDto
): Promise<Company> => {
  return apiFetch<Company>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
};

/**
 * Update company status
 */
export const updateCompanyStatus = async (
  id: string,
  status: CompanyStatus
): Promise<Company> => {
  return apiFetch<Company>(`${BASE_URL}/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
};

export interface UploadCompanyLogoResult {
  variant: LogoVariant;
  logoUrl: string;
  logoS3Key: string;
}

/**
 * Upload (or replace) one company brand asset (email | platform-light |
 * platform-dark). Sends the
 * image as multipart form-data under the field name `logo`; the server stores
 * it on S3 and returns the URL.
 */
export const uploadCompanyLogo = async (
  id: string,
  variant: LogoVariant,
  file: File
): Promise<UploadCompanyLogoResult> => {
  const formData = new FormData();
  formData.append('logo', file);
  return apiUpload<UploadCompanyLogoResult>(`${BASE_URL}/${id}/logo/${variant}`, formData);
};

/**
 * Remove one company brand asset (email | platform-light | platform-dark).
 */
export const removeCompanyLogo = async (
  id: string,
  variant: LogoVariant
): Promise<{ variant: LogoVariant; removed: boolean }> => {
  return apiFetch<{ variant: LogoVariant; removed: boolean }>(
    `${BASE_URL}/${id}/logo/${variant}`,
    { method: 'DELETE' }
  );
};

/**
 * Get company hierarchy tree
 */
export const getCompanyHierarchy = async (
  id: string
): Promise<CompanyHierarchyNode> => {
  return apiFetch<CompanyHierarchyNode>(`${BASE_URL}/${id}/hierarchy`);
};

/**
 * Get company ancestors
 */
export const getCompanyAncestors = async (id: string): Promise<Company[]> => {
  return apiFetch<Company[]>(`${BASE_URL}/${id}/ancestors`);
};

/**
 * Get company descendants
 */
export const getCompanyDescendants = async (id: string): Promise<Company[]> => {
  return apiFetch<Company[]>(`${BASE_URL}/${id}/descendants`);
};

/**
 * Get direct children
 */
export const getCompanyChildren = async (id: string): Promise<Company[]> => {
  return apiFetch<Company[]>(`${BASE_URL}/${id}/children`);
};
